import { useCallback, useEffect, useMemo } from 'react';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { getMessage } from '@gef-ui/localization/messages';
import ArrowRight from '@gef-ui/material-icons/ArrowRight';

import InfoCircleTooltip from 'components/atoms/GefMui/InfoCircleTooltip';
import { USER_ROLES } from 'constants/general';
import { startupCampaigns } from 'features/views/dashboard/campaigns/actions';
import { gtmDashboardSectionNames } from 'features/views/dashboard/constants';
import { useDetailButtonClickDataLayer } from 'features/views/dashboard/hooks';
import { getCurrentRole, getHasKmpPpzSubordinates } from 'features/views/user/selectors';
import { joinString } from 'utils/string';

import CampaignInfoGraphics from './components/CampaignsInfoGraphics';
import CampaignsTable from './components/CampaignsTable';

export const Campaigns = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const pushToClickDetailButtonDataLayer = useDetailButtonClickDataLayer();

	const currentRole = useSelector(getCurrentRole);
	const hasPPZSubordinates = useSelector(getHasKmpPpzSubordinates);
	const showSubordinatesTooltip = useMemo(
		() => currentRole === USER_ROLES.SELLER && hasPPZSubordinates,
		[currentRole, hasPPZSubordinates],
	);

	useEffect(() => {
		dispatch(startupCampaigns());
	}, [dispatch]);

	const handleRedirect = useCallback(() => {
		pushToClickDetailButtonDataLayer({
			value: gtmDashboardSectionNames.CAMPAIGNS,
		})();
		navigate('/campaigns');
	}, [pushToClickDetailButtonDataLayer, navigate]);

	const tooltipContent = useMemo(
		() =>
			showSubordinatesTooltip
				? joinString(' ', [
						getMessage('tooltips:graphDataRefreshedEveryHour'),
						getMessage('tooltips:ppzCampaignsIncluded'),
				  ])
				: getMessage('tooltips:graphDataRefreshedEveryHour'),
		[showSubordinatesTooltip],
	);

	return (
		<Paper sx={{ p: 2, borderRadius: 1 }}>
			<Stack spacing={2}>
				<Stack
					direction="row"
					sx={{
						alignItems: 'center',
						justifyContent: 'space-between',
					}}
				>
					<Stack
						direction="row"
						sx={{
							alignItems: 'center',
						}}
					>
						<Typography sx={{ fontWeight: 'bold' }} variant="body1">
							{getMessage('dashboard:campaigns.title')}
						</Typography>
						<InfoCircleTooltip sx={{ fontSize: 15 }} text={tooltipContent} />
					</Stack>
					<Button
						sx={{ width: 'max-content', justifyContent: 'left' }}
						variant="text"
						endIcon={<ArrowRight />}
						onClick={handleRedirect}
					>
						{getMessage('dashboard:detail')}
					</Button>
				</Stack>
				<CampaignInfoGraphics />
				<CampaignsTable />
			</Stack>
		</Paper>
	);
};

export default Campaigns;
